import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import useUrlState from "@/hooks/useUrlState";
import { supabase } from "@/lib/supabase";
import DashboardSidebar from "@/pages/dashboard/components/DashboardSidebar";
import MobileBottomNav from "@/pages/dashboard/components/MobileBottomNav";
import GroupPanel from "./components/GroupPanel";
import ContactTable from "./components/ContactTable";
import ContactFormModal from "./components/ContactFormModal";
import CsvImportModal from "./components/CsvImportModal";
import type { Contact, ContactGroup } from "./types";

const PAGE_SIZE = 10;

// 탭 복귀(리로드) 시 로딩 없이 마지막 주소록을 즉시 보여주기 위한 캐시
const CONTACTS_CACHE_KEY = "cache:user_contacts_list";
const GROUPS_CACHE_KEY = "cache:user_contacts_groups";

function readListCache(): Contact[] | null {
  try {
    const raw = sessionStorage.getItem(CONTACTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Contact[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeListCache(list: Contact[]): void {
  try {
    sessionStorage.setItem(CONTACTS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

function readGroupsCache(): ContactGroup[] | null {
  try {
    const raw = sessionStorage.getItem(GROUPS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ContactGroup[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeGroupsCache(list: ContactGroup[]): void {
  try {
    sessionStorage.setItem(GROUPS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패(용량 초과 등)는 무시
  }
}

export default function ContactsPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Data state (캐시가 있으면 로딩 화면 없이 즉시 복원)
  const contactsCacheRef = useRef<Contact[] | null>(readListCache());
  const groupsCacheRef = useRef<ContactGroup[] | null>(readGroupsCache());
  const [contacts, setContacts] = useState<Contact[]>(contactsCacheRef.current ?? []);
  const [groups, setGroups] = useState<ContactGroup[]>(groupsCacheRef.current ?? []);
  const [dataLoading, setDataLoading] = useState(contactsCacheRef.current === null);

  // UI state (검색·그룹·페이지를 주소(URL)에 남겨 새로고침·링크 공유에도 유지)
  const [search, setSearch] = useUrlState("q", "");
  const [groupParam, setGroupParam] = useUrlState("group", "");
  const [pageParam, setPageParam] = useUrlState("page", "1");

  const selectedGroupId = groupParam || null;
  const setSelectedGroupId = useCallback(
    (id: string | null) => {
      setGroupParam(id ?? "");
    },
    [setGroupParam]
  );

  const currentPage = Math.max(1, parseInt(pageParam, 10) || 1);
  const setCurrentPage = useCallback(
    (p: number) => {
      setPageParam(String(Math.max(1, p)));
    },
    [setPageParam]
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFormModal, setShowFormModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Contact | null>(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  // 필터가 실제로 바뀌면 첫 페이지로 (최초 복원 시에는 페이지 유지)
  const skipPageResetRef = useRef(true);
  useEffect(() => {
    if (skipPageResetRef.current) {
      skipPageResetRef.current = false;
      return;
    }
    setPageParam("1");
  }, [search, selectedGroupId, setPageParam]);
  useEffect(() => { setSelectedIds(new Set()); }, [selectedGroupId, search]);

  const showToast = (msg: string, type: "success" | "error" = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  // ── DB Fetch ─────────────────────────────────────────────────────────────
  const fetchGroups = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("contact_groups")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: true });
    if (error) { showToast("그룹 로딩 실패", "error"); return; }
    setGroups(
      (data ?? []).map((g) => ({
        id: g.id,
        name: g.name,
        color: g.color,
        description: g.description ?? undefined,
        createdAt: new Date(g.created_at).toLocaleDateString("ko-KR"),
      }))
    );
  }, [user]);

  const fetchContacts = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("contacts")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (error) { showToast("연락처 로딩 실패", "error"); return; }
    setContacts(
      (data ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        phone: c.phone,
        email: c.email ?? undefined,
        memo: c.memo ?? undefined,
        groupIds: c.group_ids ?? [],
        createdAt: new Date(c.created_at).toLocaleDateString("ko-KR"),
        lastSentAt: c.last_sent_at
          ? new Date(c.last_sent_at).toLocaleDateString("ko-KR")
          : undefined,
      }))
    );
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // 캐시가 있으면 로딩 화면 없이 조용히 갱신
    if (contactsCacheRef.current === null || groupsCacheRef.current === null) setDataLoading(true);
    Promise.all([fetchGroups(), fetchContacts()]).finally(() => {
      contactsCacheRef.current = contactsCacheRef.current ?? [];
      groupsCacheRef.current = groupsCacheRef.current ?? [];
      setDataLoading(false);
    });
  }, [user, fetchGroups, fetchContacts]);

  // 데이터가 바뀔 때마다 캐시에 저장해 탭 복귀 시 즉시 복원
  useEffect(() => {
    if (dataLoading) return;
    contactsCacheRef.current = contacts;
    writeListCache(contacts);
  }, [contacts, dataLoading]);

  useEffect(() => {
    if (dataLoading) return;
    groupsCacheRef.current = groups;
    writeGroupsCache(groups);
  }, [groups, dataLoading]);

  // ── Filtered contacts ─────────────────────────────────────────────────────
  const filteredContacts = useMemo(() => {
    let items = contacts;
    if (selectedGroupId) {
      items = items.filter((c) => c.groupIds.includes(selectedGroupId));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.phone.includes(q) ||
          (c.email?.toLowerCase().includes(q) ?? false) ||
          (c.memo?.toLowerCase().includes(q) ?? false)
      );
    }
    return items;
  }, [contacts, selectedGroupId, search]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));
  const pagedContacts = filteredContacts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // ── Contact Handlers ──────────────────────────────────────────────────────
  const handleAddContact = async (data: Omit<Contact, "id" | "createdAt">) => {
    if (!user) return;
    const { error } = await supabase.from("contacts").insert({
      user_id: user.id,
      name: data.name,
      phone: data.phone,
      email: data.email ?? null,
      memo: data.memo ?? null,
      group_ids: data.groupIds,
    });
    if (error) { showToast("연락처 추가 실패", "error"); return; }
    await fetchContacts();
    setShowFormModal(false);
    showToast("연락처가 추가되었습니다");
  };

  const handleEditContact = async (data: Omit<Contact, "id" | "createdAt">) => {
    if (!editTarget) return;
    const { error } = await supabase
      .from("contacts")
      .update({
        name: data.name,
        phone: data.phone,
        email: data.email ?? null,
        memo: data.memo ?? null,
        group_ids: data.groupIds,
        updated_at: new Date().toISOString(),
      })
      .eq("id", editTarget.id)
      .eq("user_id", user?.id ?? "");
    if (error) { showToast("연락처 수정 실패", "error"); return; }
    await fetchContacts();
    setShowFormModal(false);
    setEditTarget(null);
    showToast("연락처가 수정되었습니다");
  };

  const handleDeleteContact = async (id: string) => {
    const { error } = await supabase
      .from("contacts")
      .delete()
      .eq("id", id)
      .eq("user_id", user?.id ?? "");
    if (error) { showToast("연락처 삭제 실패", "error"); return; }
    setContacts((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    setDeleteConfirmId(null);
    showToast("연락처가 삭제되었습니다");
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const { error } = await supabase
      .from("contacts")
      .delete()
      .in("id", ids)
      .eq("user_id", user?.id ?? "");
    if (error) { showToast("일괄 삭제 실패", "error"); return; }
    setContacts((prev) => prev.filter((c) => !selectedIds.has(c.id)));
    const count = selectedIds.size;
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    showToast(`${count}명의 연락처가 삭제되었습니다`);
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleAll = () => {
    const pageIds = pagedContacts.map((c) => c.id);
    const allSelected = pageIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) { pageIds.forEach((id) => next.delete(id)); }
      else { pageIds.forEach((id) => next.add(id)); }
      return next;
    });
  };

  const handleSendTo = () => {
    navigate("/dashboard/send");
  };

  const handleCsvImport = async (newContacts: Omit<Contact, "id" | "createdAt">[], _groupIds: string[]) => {
    if (!user) return;
    const rows = newContacts.map((c) => ({
      user_id: user.id,
      name: c.name,
      phone: c.phone,
      email: c.email ?? null,
      memo: c.memo ?? null,
      group_ids: c.groupIds,
    }));
    const { error } = await supabase.from("contacts").insert(rows);
    if (error) { showToast("CSV 가져오기 실패", "error"); return; }
    await fetchContacts();
    setShowCsvModal(false);
    showToast(`${rows.length}명의 연락처를 가져왔습니다`);
  };

  // ── Group Handlers ────────────────────────────────────────────────────────
  const handleAddGroup = async (g: Omit<ContactGroup, "id" | "createdAt">) => {
    if (!user) return;
    const { error } = await supabase.from("contact_groups").insert({
      user_id: user.id,
      name: g.name,
      color: g.color,
      description: g.description ?? null,
    });
    if (error) { showToast("그룹 추가 실패", "error"); return; }
    await fetchGroups();
    showToast("그룹이 추가되었습니다");
  };

  const handleEditGroup = async (g: ContactGroup) => {
    const { error } = await supabase
      .from("contact_groups")
      .update({ name: g.name, color: g.color, description: g.description ?? null })
      .eq("id", g.id)
      .eq("user_id", user?.id ?? "");
    if (error) { showToast("그룹 수정 실패", "error"); return; }
    await fetchGroups();
    showToast("그룹이 수정되었습니다");
  };

  const handleDeleteGroup = async (id: string) => {
    const { error } = await supabase
      .from("contact_groups")
      .delete()
      .eq("id", id)
      .eq("user_id", user?.id ?? "");
    if (error) { showToast("그룹 삭제 실패", "error"); return; }

    const affectedContacts = contacts.filter((c) => c.groupIds.includes(id));
    for (const c of affectedContacts) {
      await supabase
        .from("contacts")
        .update({ group_ids: c.groupIds.filter((gid) => gid !== id) })
        .eq("id", c.id);
    }

    await Promise.all([fetchGroups(), fetchContacts()]);
    if (selectedGroupId === id) setSelectedGroupId(null);
    showToast("그룹이 삭제되었습니다");
  };

  // ── CSV Export ────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const exportList = selectedIds.size > 0
      ? contacts.filter((c) => selectedIds.has(c.id))
      : filteredContacts;
    const header = "이름,전화번호,이메일,메모";
    const rows = exportList.map((c) => `${c.name},${c.phone},${c.email ?? ""},${c.memo ?? ""}`);
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
    showToast(`${exportList.length}명의 연락처를 내보냈습니다`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-violet-400 text-3xl"></i>
      </div>
    );
  }
  if (!user) return null;

  const sidebarWidth = collapsed ? "md:ml-16" : "md:ml-60";

  return (
    <div className="min-h-screen bg-[#080812] flex">
      <DashboardSidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <main className={`flex-1 ${sidebarWidth} transition-all duration-300 min-h-screen pb-20 md:pb-0`}>
        {/* ── Top bar ── */}
        <header className="h-14 md:h-16 bg-[#0a0a18]/80 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-3 md:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-base md:text-lg"></i>
            </button>
            <div>
              <h1 className="text-white font-semibold text-sm md:text-base">주소록</h1>
              <p className="text-gray-500 text-[10px] md:text-xs hidden sm:block">연락처 관리</p>
            </div>
          </div>

          {/* Desktop action buttons */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-download-line text-sm"></i>
              <span className="hidden md:inline">내보내기</span>
            </button>
            <button
              onClick={() => setShowCsvModal(true)}
              className="flex items-center gap-1.5 bg-white/[0.05] border border-white/[0.08] text-gray-300 hover:text-white px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
            >
              <i className="ri-file-upload-line text-sm"></i>
              <span className="hidden md:inline">CSV 가져오기</span>
            </button>
            <button
              onClick={() => { setEditTarget(null); setShowFormModal(true); }}
              className="flex items-center gap-1.5 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white px-3 md:px-4 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all hover:scale-105"
            >
              <i className="ri-user-add-line text-sm"></i>
              <span className="hidden md:inline">연락처 추가</span>
            </button>
          </div>

          {/* Mobile action buttons */}
          <div className="flex sm:hidden items-center gap-1.5">
            <button
              onClick={handleExportCSV}
              className="w-8 h-8 flex items-center justify-center bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white rounded-lg cursor-pointer transition-all"
            >
              <i className="ri-download-line text-sm"></i>
            </button>
            <button
              onClick={() => setShowCsvModal(true)}
              className="w-8 h-8 flex items-center justify-center bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white rounded-lg cursor-pointer transition-all"
            >
              <i className="ri-file-upload-line text-sm"></i>
            </button>
            <button
              onClick={() => { setEditTarget(null); setShowFormModal(true); }}
              className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white rounded-lg cursor-pointer transition-all"
            >
              <i className="ri-user-add-line text-sm"></i>
            </button>
          </div>
        </header>

        {/* ── Main content ── */}
        <div className="p-3 md:p-6 flex flex-col lg:flex-row gap-3 md:gap-5">
          {/* Group Panel - Desktop: sidebar style, Mobile: horizontal chips */}
          <div className="hidden lg:block w-52 shrink-0">
            <div className="h-[calc(100vh-128px)]">
              <GroupPanel
                groups={groups}
                contacts={contacts}
                selectedGroupId={selectedGroupId}
                onSelectGroup={setSelectedGroupId}
                onAddGroup={handleAddGroup}
                onEditGroup={handleEditGroup}
                onDeleteGroup={handleDeleteGroup}
              />
            </div>
          </div>

          {/* Right: Contact list */}
          <div className="flex-1 min-w-0 flex flex-col gap-3 md:gap-4">
            {/* Mobile group chips bar */}
            <div className="lg:hidden">
              <GroupPanel
                groups={groups}
                contacts={contacts}
                selectedGroupId={selectedGroupId}
                onSelectGroup={setSelectedGroupId}
                onAddGroup={handleAddGroup}
                onEditGroup={handleEditGroup}
                onDeleteGroup={handleDeleteGroup}
                compact
              />
            </div>

            {/* Search & bulk actions bar */}
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[140px]">
                <div className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                  <i className="ri-search-line text-gray-500 text-xs md:text-sm"></i>
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="이름, 번호, 이메일 검색..."
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl pl-8 md:pl-9 pr-3 md:pr-4 py-2 md:py-2.5 text-white text-xs md:text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/50 transition-all"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 md:right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
                  >
                    <i className="ri-close-line text-xs md:text-sm"></i>
                  </button>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs text-gray-500 whitespace-nowrap">
                <span className="hidden sm:inline">
                  {selectedGroupId
                    ? groups.find((g) => g.id === selectedGroupId)?.name
                    : "전체"}
                </span>
                <span className="hidden sm:inline text-gray-700">·</span>
                <span className="text-white font-semibold">{filteredContacts.length}</span>명
              </div>
            </div>

            {/* Bulk actions */}
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-violet-400 text-[10px] md:text-xs font-semibold whitespace-nowrap">
                  {selectedIds.size}명 선택됨
                </span>
                <button
                  onClick={() => navigate("/dashboard/send")}
                  className="flex items-center gap-1 md:gap-1.5 bg-violet-500/20 border border-violet-500/30 text-violet-400 hover:bg-violet-500/30 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className="ri-send-plane-line text-xs md:text-sm"></i>
                  <span className="hidden sm:inline">문자 발송</span>
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(true)}
                  className="flex items-center gap-1 md:gap-1.5 bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className="ri-delete-bin-line text-xs md:text-sm"></i>
                  <span className="hidden sm:inline">삭제</span>
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-gray-500 hover:text-white text-[10px] md:text-xs cursor-pointer whitespace-nowrap transition-colors"
                >
                  선택 해제
                </button>
              </div>
            )}

            {/* Table / Card List */}
            {dataLoading ? (
              <div className="flex-1 flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-violet-400 text-2xl"></i>
                  <p className="text-gray-500 text-sm">연락처 불러오는 중...</p>
                </div>
              </div>
            ) : (
              <ContactTable
                contacts={pagedContacts}
                groups={groups}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onToggleAll={handleToggleAll}
                onEdit={(c) => { setEditTarget(c); setShowFormModal(true); }}
                onDelete={(id) => setDeleteConfirmId(id)}
                onSendTo={handleSendTo}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      </main>

      <MobileBottomNav />

      {/* Modals */}
      <ContactFormModal
        open={showFormModal}
        editTarget={editTarget}
        groups={groups}
        onClose={() => { setShowFormModal(false); setEditTarget(null); }}
        onSave={editTarget ? handleEditContact : handleAddContact}
      />

      <CsvImportModal
        open={showCsvModal}
        groups={groups}
        onClose={() => setShowCsvModal(false)}
        onImport={handleCsvImport}
      />

      {/* Single delete confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)}></div>
          <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-2xl p-5 md:p-6 w-full max-w-sm text-center">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-3 md:mb-4">
              <i className="ri-user-unfollow-line text-red-400 text-xl md:text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-sm md:text-base mb-1 md:mb-2">연락처를 삭제할까요?</h3>
            <p className="text-gray-400 text-xs md:text-sm mb-5 md:mb-6">삭제 후에는 복구할 수 없습니다.</p>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              >
                취소
              </button>
              <button
                onClick={() => handleDeleteContact(deleteConfirmId)}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-xs md:text-sm font-bold cursor-pointer whitespace-nowrap transition-all"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirm */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setBulkDeleteConfirm(false)}></div>
          <div className="relative bg-[#0d0d1a] border border-white/[0.1] rounded-2xl p-5 md:p-6 w-full max-w-sm text-center">
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-3 md:mb-4">
              <i className="ri-group-line text-red-400 text-xl md:text-2xl"></i>
            </div>
            <h3 className="text-white font-bold text-sm md:text-base mb-1 md:mb-2">{selectedIds.size}명을 삭제할까요?</h3>
            <p className="text-gray-400 text-xs md:text-sm mb-5 md:mb-6">선택한 연락처가 모두 삭제됩니다.</p>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={() => setBulkDeleteConfirm(false)}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-white/[0.06] text-gray-300 hover:text-white text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              >
                취소
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 py-2 md:py-2.5 rounded-xl bg-red-500/20 border border-red-500/30 text-red-400 hover:bg-red-500/30 text-xs md:text-sm font-bold cursor-pointer whitespace-nowrap transition-all"
              >
                전체 삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-20 md:bottom-6 right-4 md:right-6 z-[60] flex items-center gap-2 md:gap-2.5 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border transition-all ${
          toast.type === "success"
            ? "bg-emerald-500/20 border-emerald-500/30 text-emerald-300"
            : "bg-red-500/20 border-red-500/30 text-red-300"
        }`}>
          <i className={`${toast.type === "success" ? "ri-checkbox-circle-line" : "ri-close-circle-line"} text-sm md:text-base`}></i>
          <span className="text-xs md:text-sm font-medium">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}